/**
 * Centralised React Query key factory for the component-rules feature.
 *
 * All hooks in this directory and every test that needs to invalidate or
 * pre-seed a cache should import from here. A single factory keeps the
 * key shapes consistent and makes it impossible to drift between a hook's
 * internal `queryKey` and the invalidation call in a sibling mutation.
 *
 * The keys follow the project-wide convention used elsewhere in
 * `src/modules/admin`: `['admin', <resource>, ...scope]`. We use
 * `'component-rules'` as the top-level resource slug for both the rule
 * endpoints and the audit log, and `'component-prop-labels'` for the
 * sibling prop labels endpoint.
 */

import type { RuleStatus } from '@components/shared/configurable';

/**
 * Query keys for component-rules queries.
 *
 * Each factory returns a `readonly` tuple so TanStack Query can compare
 * them by structural equality without copying. `keyof` / `typeof` make it
 * safe to derive narrower prefixes when invalidating a whole family at
 * once (e.g., "every rule under this component id").
 */
export const componentRulesKeys = {
  /** Root slug shared by every rule-related query. */
  all: ['admin', 'component-rules'] as const,

  /** All rule lists, indexed by component id. */
  lists: () => [...componentRulesKeys.all, 'list'] as const,

  /** A specific rule list, optionally filtered by status. */
  list: (componentId: string, status?: RuleStatus) =>
    [
      ...componentRulesKeys.lists(),
      componentId,
      ...(status === undefined ? [] : [status]),
    ] as const,

  /**
   * Module-scoped (or fully unscoped) rule list. Powers the raw-JSON
   * CRUD surface on the admin module edit page — distinct from `list()`
   * so the module-scoped query and the per-component query cache
   * independently. The `componentId` filter is optional; pass
   * `'any'` to indicate "no filter".
   */
  listAll: (componentId?: string, status?: RuleStatus) =>
    [
      ...componentRulesKeys.lists(),
      'all',
      componentId ?? 'any',
      status ?? 'any',
    ] as const,

  /** All individual rule details. */
  details: () => [...componentRulesKeys.all, 'detail'] as const,

  /** A specific rule detail by id. */
  detail: (id: string) => [...componentRulesKeys.details(), id] as const,

  /** Audit log for a component. */
  auditLog: (componentId: string) =>
    [...componentRulesKeys.all, 'audit-log', componentId] as const,
};

/**
 * Query keys for the component prop-labels endpoint.
 *
 * Kept separate from `componentRulesKeys` so invalidating rules does not
 * needlessly refetch labels (and vice-versa), while still sharing the
 * `admin` prefix for top-level cache wipes.
 */
export const componentPropLabelsKeys = {
  all: ['admin', 'component-prop-labels'] as const,
  list: (componentId: string) =>
    [...componentPropLabelsKeys.all, componentId] as const,
};
